import clsx from 'clsx'
import type { Connector, ConnectorStatus, ISODate } from '@app/shared'

import { useSyncConnector } from '../../lib/data'
import { GmailCard } from './GmailCard'
import { Envelope, OuraRing, RedditFace } from './icons'
import type { MailResult } from './mail'
import { lastSyncLabel } from './model'
import { ActionButton, StatPair, StatusPill } from './parts'

// ============================================================================
// ONE CONNECTOR, STATING ITS OWN LAST-KNOWN TRUTH: what it reaches, when it last
// worked, how much it has ever brought in, and — in plain words, in a tinted
// strip nobody can skim past — what is wrong with it or what it will never know.
//
// The four statuses are four different cards, not one card with a colour swap:
//   ok            a caveat in neutral grey, `Sync now`
//   error         a red border, what broke in one sentence, `Try again`
//   rate limited  amber, the retry time, and NO button — there is nothing to do
//   planned       dashed, grey, inert, and honest about never having connected
// (`disconnected` is Gmail's alone so far, and Gmail has its own card: GmailCard.tsx.)
// ============================================================================

const PILL: Record<ConnectorStatus, { tone: 'success' | 'danger' | 'warning' | 'neutral'; label: string } | null> = {
  ok: { tone: 'success', label: 'OK' },
  error: { tone: 'danger', label: 'ERROR' },
  rate_limited: { tone: 'warning', label: 'RATE LIMITED' },
  planned: null,
  disconnected: { tone: 'neutral', label: 'NOT CONNECTED' },
}

const NOTE_STRIP: Record<string, string> = {
  error: 'bg-danger-fill text-danger font-medium',
  warning: 'bg-warning-fill text-warning font-medium',
  neutral: 'bg-surface-2 text-ink-3 font-normal',
}

/** Placeholder marks — the handoff ships no brand logos (§Assets). */
function ConnectorLogo({ connector }: { connector: Connector }) {
  const base = 'flex size-[38px] flex-none items-center justify-center rounded-row'

  if (connector.status === 'planned') {
    const Mark = connector.id === 'gmail' ? Envelope : OuraRing
    return (
      <div className={clsx(base, 'bg-fill')}>
        <Mark size={19} strokeWidth={1.8} className="text-faint-2" />
      </div>
    )
  }

  if (connector.id === 'reddit') {
    return (
      <div className={clsx(base, 'border border-orange-line bg-orange-fill')}>
        <RedditFace size={19} strokeWidth={1.8} className="text-orange-2" />
      </div>
    )
  }

  // Canvas, and anything else without a drawn mark: a monogram in the service's
  // own tint. Deliberately not a coloured square — the handoff's icons rule.
  return (
    <div
      className={clsx(
        base,
        'border border-danger-line-2 bg-danger-fill text-[14px] font-bold leading-none text-danger-dot',
      )}
      aria-hidden
    >
      {connector.shortName.charAt(0)}
    </div>
  )
}

export function ConnectorCard({
  connector,
  today,
  nowMinutes,
  mailResult,
}: {
  connector: Connector
  today: ISODate
  nowMinutes: number
  /** The Google sign-in's `?mail=` outcome — only the Gmail card reads it. */
  mailResult?: MailResult | null
}) {
  // Called unconditionally, ahead of the planned-connector early return, so this component never
  // calls hooks conditionally — a planned card just never triggers the mutation.
  const sync = useSyncConnector()

  if (connector.status === 'planned') return <PlannedConnectorCard connector={connector} />
  // Gmail is set up FROM this card (Google sign-in, per-account disconnect), so it has its own.
  if (connector.id === 'gmail') {
    return <GmailCard connector={connector} mailResult={mailResult} today={today} nowMinutes={nowMinutes} />
  }

  const pill = PILL[connector.status]
  const lastSync = lastSyncLabel(connector, today, nowMinutes)

  return (
    <article
      className={clsx(
        'flex flex-col gap-3 rounded-card border bg-surface px-[17px] py-[15px]',
        connector.status === 'error' ? 'border-danger-line' : 'border-line',
      )}
    >
      <div className="flex items-start gap-3">
        <ConnectorLogo connector={connector} />
        <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
          <div className="flex items-center gap-2">
            <h3 className="text-[13.5px] font-semibold leading-none">{connector.name}</h3>
            {/* 13c: the source dot pings once when this connector's last-sync stamp moves —
                the arrival announcing itself, in the one place that names the source. */}
            {pill && <StatusPill tone={pill.tone} label={pill.label} pingKey={lastSync ?? ''} />}
          </div>
          <p className="text-[11px] font-normal leading-[1.4] text-microcopy">{connector.scope}</p>
        </div>
      </div>

      {connector.note && (
        <p
          className={clsx(
            'rounded-tile px-3 py-[10px] text-[11.5px] leading-[1.4] text-pretty',
            NOTE_STRIP[connector.note.tone],
          )}
        >
          {connector.note.text}
        </p>
      )}

      <div className="flex items-center gap-5">
        {/* A stat pair whose value is unknown is left out, not filled with a dash. */}
        {lastSync && <StatPair label="LAST SYNC" value={lastSync} />}
        {connector.itemsPulledAllTime !== null && (
          <StatPair label="ITEMS PULLED" value={`${connector.itemsPulledAllTime} all-time`} />
        )}
        <div className="ml-auto flex items-center gap-[7px]">
          <PrimaryAction connector={connector} sync={sync} />
          <ActionButton
            label="Disconnect"
            variant="quiet"
            waitingFor={`Disconnecting ${connector.shortName} deletes its token off the Worker, which is deliberately a command-line act: a button that destroys a credential is a button that gets pressed by accident.`}
          />
        </div>
      </div>

      {/* A failed on-demand sync — `Sync now` on a healthy card, `Try again` on a broken one —
          gets its own sentence rather than vanishing into the console. The note strip above is the
          connector's LAST KNOWN state; this is about the click that just happened, and on the
          error card it is how a retry that failed again says so instead of quietly resetting. */}
      {sync.error && (
        <p className="text-[11px] font-normal leading-[1.4] text-danger text-pretty">
          {sync.error instanceof Error
            ? sync.error.message
            : 'That sync could not be started, and the reason did not come back with a sentence attached.'}
        </p>
      )}
    </article>
  )
}

function PrimaryAction({
  connector,
  sync,
}: {
  connector: Connector
  sync: ReturnType<typeof useSyncConnector>
}) {
  if (connector.status === 'error') {
    // LIVE, and deliberately a RETRY rather than a reconnect.
    //
    // This button used to read `Reconnect` and do nothing — the sentence explaining why lived in
    // a hover tooltip, which on a red button reads as a broken app to anyone who does not think
    // to hover. Worse, reconnecting was the wrong offer: `error` means the last sync failed, not
    // that the credential expired, and bCourses has answered 406 to a token months from its
    // expiry. Swapping a working token for another working token would have changed nothing.
    //
    // Re-running the sync is the only recovery act that is honest for every cause AND that the
    // owner can perform without a terminal, so it is the one offered. It stays `dark` because
    // this is still the error card's primary action. Replacing a token remains a command-line
    // act; what went wrong stays in the connector's own note above.
    return (
      <ActionButton
        label={sync.isPending ? 'Trying again…' : 'Try again'}
        variant="dark"
        busy={sync.isPending}
        onClick={() => sync.mutate(connector.id)}
      />
    )
  }
  if (connector.status === 'rate_limited') {
    // NO BUTTON, by design. The retry is already scheduled; offering an action
    // here would be offering the owner a way to make it worse.
    return <ActionButton label="Retrying…" variant="inert" />
  }
  // LIVE: POST /api/connections/canvas/sync runs the same adapter the six-hourly cron runs.
  // Demo mode's syncConnector refuses in words (no cloud behind the fixture) — the error strip
  // below the actions row is what shows that refusal, and the button itself is not disabled for
  // it, because the honest answer belongs in the sentence, not in a dead button.
  return (
    <ActionButton
      label={sync.isPending ? 'Syncing…' : 'Sync now'}
      variant="outline"
      busy={sync.isPending}
      onClick={() => sync.mutate(connector.id)}
    />
  )
}

/**
 * PLANNED IS NOT TEASED. Dashed border, grey text, a pill naming the version,
 * and a button that is visibly incapable of being pressed — the card exists to
 * say "we know, and not yet", which is a different message from a live-looking
 * card that fails when you touch it.
 */
function PlannedConnectorCard({ connector }: { connector: Connector }) {
  return (
    <article className="flex items-center gap-3 rounded-card border border-dashed border-dash bg-surface-2 px-[17px] py-[15px]">
      <ConnectorLogo connector={connector} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <h3 className="text-[13.5px] font-semibold leading-none text-muted">{connector.name}</h3>
          <StatusPill tone="neutral" dot={false} label={`PLANNED · ${connector.plannedFor ?? 'LATER'}`} />
        </div>
        <p className="text-[11px] font-normal leading-[1.4] text-microcopy">{connector.scope}</p>
      </div>
      <span className="flex-none cursor-default rounded-button bg-fill px-3 py-[6px] text-[11px] font-medium leading-none text-microcopy">
        Not available yet
      </span>
    </article>
  )
}
